/**
 * Regole pure dello storico dei giudizi (F-135).
 *
 * In un modulo a parte come `next-run-format.ts`: sono le due cose di questa
 * pagina che si possono sbagliare senza che nulla si rompa a schermo — un
 * conteggio storto e una griglia di giorni con un buco — e qui si provano
 * senza montare niente.
 */

/** Tutti i giorni di calendario fra i due estremi, inclusi, in `YYYY-MM-DD`. */
export function daysBetween(from: string, to: string): string[] {
  const out: string[] = [];
  const cursor = new Date(`${from}T00:00:00.000Z`);
  const end = new Date(`${to}T00:00:00.000Z`);
  if (Number.isNaN(cursor.getTime()) || Number.isNaN(end.getTime())) return out;
  // Un limite di sicurezza: una finestra assurda non deve poter bloccare la
  // pagina in un ciclo. Le finestre offerte arrivano a 90 giorni.
  while (cursor <= end && out.length < 400) {
    out.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return out;
}

/**
 * Quante volte il giudizio è **cambiato** lungo la serie.
 *
 * ⚠️ Non è «quanti giudizi diversi ci sono stati»: Buy → Hold → Buy sono due
 * cambi e due valori distinti, e le due letture divergono appena il modello
 * torna sui suoi passi — che è il caso interessante. Si contano i cambi, perché
 * la domanda è quanto è stato ondivago.
 */
export function countRatingChanges(points: readonly { rating: string }[]): number {
  let changes = 0;
  for (let index = 1; index < points.length; index += 1) {
    if (points[index]!.rating !== points[index - 1]!.rating) changes += 1;
  }
  return changes;
}

/**
 * Le gronde laterali sono **esportate**: la timeline dei giudizi le applica
 * come proprie, così un giorno sta alla stessa ascissa in tutti e tre i
 * riquadri. Se cambiano qui, cambiano là — e non possono divergere.
 */
export const CHART_GUTTERS = { left: 72, right: 16 } as const;
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * L'ascissa di un giorno dentro la finestra: il **centro** della sua cella.
 *
 * La timeline disegna una cella per giorno; il punto del grafico deve cadere
 * al centro di quella cella, non sul suo bordo sinistro — altrimenti i tre
 * riquadri sarebbero sfasati di mezza giornata, poco visibile e abbastanza per
 * far leggere il punto nel giorno sbagliato.
 */
export function dayOffset(date: string, from: string, to: string): number {
  const start = Date.parse(`${from}T00:00:00.000Z`);
  const end = Date.parse(`${to}T00:00:00.000Z`) + DAY_MS;
  const span = end - start || 1;
  return (Date.parse(`${date.slice(0, 10)}T00:00:00.000Z`) + DAY_MS / 2 - start) / span;
}

/** Il giorno sotto un'ascissa relativa (0-1), o `null` fuori dalla finestra. */
export function dayAtOffset(offset: number, from: string, to: string): string | null {
  if (offset < 0 || offset > 1) return null;
  const start = Date.parse(`${from}T00:00:00.000Z`);
  const end = Date.parse(`${to}T00:00:00.000Z`) + DAY_MS;
  const days = Math.round((end - start) / DAY_MS);
  const index = Math.min(days - 1, Math.floor(offset * days));
  return new Date(start + index * DAY_MS).toISOString().slice(0, 10);
}
